// pages/developer/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { FiSun, FiMoon, FiUser, FiLock, FiMail, FiUserPlus, FiCode } from 'react-icons/fi';
import toast from 'react-hot-toast';

function DeveloperLogin() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
  });
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Clear any invalid storage on login page load
  useEffect(() => {
    const token = localStorage.getItem('dev_token');
    const user = localStorage.getItem('dev_user');
    
    if (token === 'undefined' || token === 'null') {
      localStorage.removeItem('dev_token');
    }
    if (user === 'undefined' || user === 'null') {
      localStorage.removeItem('dev_user');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const success = await login(formData.username, formData.password);
        if (success) {
          navigate('/developer/dashboard');
        }
      } else {
        const success = await register({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName || formData.username,
          role: 'developer',
        });
        if (success) {
          setIsLogin(true);
          setFormData({ ...formData, password: '' });
          toast.success('Registration successful! Please login.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card developer-card">
        <div className="login-header">
          <h1>🚚 Delivery7</h1>
          <button className="theme-toggle" onClick={toggleTheme} type="button">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
        </div>
        
        <div className="login-role-badge developer-badge">
          <FiCode /> Developer Portal
        </div>
        
        <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
        <p className="login-subtitle">
          {isLogin ? 'Login to your developer account' : 'Register as a developer'}
        </p>

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div className="form-group">
                <label>
                  <FiUser style={{ marginRight: '6px' }} />
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <FiMail style={{ marginRight: '6px' }} />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </>
          )}
          
          <div className="form-group">
            <label>
              <FiUser style={{ marginRight: '6px' }} />
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="Enter your username"
              required
            />
          </div>
          
          <div className="form-group">
            <label>
              <FiLock style={{ marginRight: '6px' }} />
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter your password"
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Loading...' : (isLogin ? '🔐 Login' : '🚀 Register')}
          </button>
        </form>

        <div className="login-footer">
          <button
            type="button"
            className="switch-btn"
            onClick={() => {
              setIsLogin(!isLogin);
              setFormData({ ...formData, password: '' });
            }}
          >
            {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
          </button>
        </div>

        <div className="login-role-badge">
          <span>🔑 Developer Access Only</span>
        </div>
      </div>

      <style>{`
        .login-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--bg-primary);
          padding: 20px;
        }
        
        .login-card {
          background: var(--bg-card);
          border-radius: var(--radius);
          border: 1px solid var(--border-color);
          padding: 40px;
          max-width: 420px;
          width: 100%;
          box-shadow: var(--shadow);
        }
        
        .developer-card {
          border-top: 4px solid #667eea;
        }
        
        .login-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .login-header h1 {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .login-header .theme-toggle {
          background: none;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 6px 10px;
          cursor: pointer;
          color: var(--text-primary);
          font-size: 18px;
          transition: var(--transition);
        }
        
        .login-header .theme-toggle:hover {
          background: var(--bg-hover);
        }
        
        .login-role-badge {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .developer-badge {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          color: #667eea;
          background: rgba(102, 126, 234, 0.15);
          border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .login-card h2 {
          font-size: 24px;
          margin-bottom: 4px;
        }
        
        .login-subtitle {
          color: var(--text-muted);
          margin-bottom: 24px;
          font-size: 14px;
        }
        
        .login-card form .btn {
          width: 100%;
          padding: 12px;
          font-size: 16px;
          justify-content: center;
        }
        
        .login-footer {
          margin-top: 16px;
          text-align: center;
        }
        
        .login-footer .switch-btn {
          background: none;
          border: none;
          color: var(--accent);
          cursor: pointer;
          font-size: 14px;
          transition: var(--transition);
        }
        
        .login-footer .switch-btn:hover {
          text-decoration: underline;
        }
        
        .login-role-badge {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
          text-align: center;
        }
        
        .login-role-badge span {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          background: var(--bg-input);
        }
        
        .form-group {
          margin-bottom: 16px;
        }
        
        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 4px;
          color: var(--text-secondary);
        }
        
        .form-group input {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-size: 14px;
          transition: var(--transition);
        }
        
        .form-group input:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-light);
        }
        
        .form-group input::placeholder {
          color: var(--text-muted);
        }
        
        .btn {
          padding: 8px 20px;
          border: none;
          border-radius: var(--radius-sm);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--font-family);
          line-height: 1.5;
        }
        
        .btn-primary {
          background: var(--accent);
          color: white;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: var(--accent-hover);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </div>
  );
}

export default DeveloperLogin;